import type { PermissionAction, PermissionFeature } from '@/utils/permissions';

export type BackofficePermissionRequirement = {
  feature: PermissionFeature;
  action: PermissionAction;
};

const list = (feature: PermissionFeature): BackofficePermissionRequirement => ({
  feature,
  action: 'list',
});

/** Top-level Back Office sections → permission required to show the section. */
export const BACKOFFICE_SECTION_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  'feature-flags-references': list('feature_flags'),
  'feature-testing': list('feature_flags'),
  'bookings-dashboard': list('google_calendar_bookings'),
  'doctor-assignment': list('doctor_assignment'),
  'organization-references': list('organizations'), // overridden: section visible if ANY subtab allowed
  'clinical-references': list('references_signs'),
  'medical-references': list('references_pharmaceuticals'),
  'lifestyle-references': list('references_diets'),
  'membership-references': list('references_memberships'),
  references: list('catalog_registry'),
  'workflow-references': list('workflow_tasks'),
  'communications-references': list('notification_templates'),
  'prompt-management': list('prompt_management'),
  'prompt-categories': list('prompt_categories'),
  'prompt-lookup': list('prompt_management'),
  'prompt-actions': list('prompt_actions'),
};

/** Orgs & Partners subtabs. */
export const ORG_PARTNER_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  organizations: list('organizations'),
  clinics: list('clinics'),
  partners: list('partners_catalog'),
  'partner-tests': list('reference_integrations'),
};

/** Clinical subtabs. */
export const CLINICAL_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  biomarkers: list('references_biomarkers'),
  'biomarker-ranges': list('references_biomarkers'),
  'code-mappings': list('catalog_registry'), // No Access for Clinical Support
  pathways: list('references_protocols'),
  signs: list('references_signs'),
  symptoms: list('references_symptoms'),
  diagnoses: list('diagnosis_analysis'),
  gender: list('references_genders'),
  phases: list('references_phases'),
  'wearable-thresholds': list('wearable_thresholds'),
};

/** Medical subtabs. */
export const MEDICAL_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  pharmaceuticals: list('references_pharmaceuticals'),
  nutraceuticals: list('references_nutraceuticals'),
  labs: list('references_labs'),
  vaccines: list('references_vaccines'),
};

/** Lifestyle subtabs. */
export const LIFESTYLE_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  diets: list('references_diets'),
  exercises: list('references_exercises'),
  therapies: list('references_therapies'),
  'lifestyle-add-ons': list('references_addons'),
  advices: list('references_advices'),
  'journal-categories': list('references_journal_categories'),
  'journal-questions': list('references_journal_categories'),
};

/** Membership subtabs. */
export const MEMBERSHIP_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  memberships: list('references_programs'),
  addons: list('references_addons'),
  'discount-codes': list('discount_codes'),
};

/** Catalog & Codes subtabs. */
export const CATALOG_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  catalog: list('catalog_registry'),
  registry: list('catalog_registry'),
};

/** Workflow subtabs. */
export const WORKFLOW_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  tasks: list('workflow_tasks'),
  checklists: list('workflow_checklists'),
  templates: list('program_templates'),
  teams: list('workflow_teams'),
};

/** Communications subtabs. */
export const COMMUNICATIONS_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  send: list('communication_send'),
  bulk: list('communication_bulk'),
  schedule: list('notification_templates'),
  templates: list('notification_templates'),
  history: list('user_notifications'),
};

/** Prompt management subtabs. */
export const PROMPT_SUBTAB_PERMISSIONS: Record<string, BackofficePermissionRequirement> = {
  'prompt-management': list('prompt_management'),
  'prompt-categories': list('prompt_categories'),
  'prompt-lookup': list('prompt_management'),
  'prompt-actions': list('prompt_actions'),
};

export const BACKOFFICE_SUBTAB_PERMISSIONS_BY_SECTION: Record<
  string,
  Record<string, BackofficePermissionRequirement>
> = {
  'organization-references': ORG_PARTNER_SUBTAB_PERMISSIONS,
  'clinical-references': CLINICAL_SUBTAB_PERMISSIONS,
  'medical-references': MEDICAL_SUBTAB_PERMISSIONS,
  'lifestyle-references': LIFESTYLE_SUBTAB_PERMISSIONS,
  'membership-references': MEMBERSHIP_SUBTAB_PERMISSIONS,
  references: CATALOG_SUBTAB_PERMISSIONS,
  'workflow-references': WORKFLOW_SUBTAB_PERMISSIONS,
  'communications-references': COMMUNICATIONS_SUBTAB_PERMISSIONS,
  'prompt-management': PROMPT_SUBTAB_PERMISSIONS,
};

export type PermissionChecker = (feature: PermissionFeature, action: PermissionAction) => boolean;

export const canAccessBackofficeRequirement = (
  requirement: BackofficePermissionRequirement | undefined,
  hasPermission: PermissionChecker
): boolean => {
  // A subtab inside a known section with no row of its own (Citations,
  // Specialist Recommendations) stays visible. Unknown sections are denied
  // in canAccessBackofficeSection.
  if (!requirement) return true;
  return hasPermission(requirement.feature, requirement.action);
};

/** Ordered list of subtab values the user may open for a section. */
export const getAllowedBackofficeSubtabs = (
  sectionTab: string,
  hasPermission: PermissionChecker,
  orderedSubtabs: readonly string[]
): string[] => {
  const requirements = BACKOFFICE_SUBTAB_PERMISSIONS_BY_SECTION[sectionTab];
  if (!requirements) return [...orderedSubtabs];
  return orderedSubtabs.filter((value) =>
    canAccessBackofficeRequirement(requirements[value], hasPermission)
  );
};

/** Section is visible if any mapped subtab is allowed (or section-level gate). */
export const canAccessBackofficeSection = (
  sectionTab: string,
  hasPermission: PermissionChecker
): boolean => {
  const subtabs = BACKOFFICE_SUBTAB_PERMISSIONS_BY_SECTION[sectionTab];
  if (subtabs) {
    return Object.values(subtabs).some((req) => canAccessBackofficeRequirement(req, hasPermission));
  }
  const sectionRequirement = BACKOFFICE_SECTION_PERMISSIONS[sectionTab];
  if (!sectionRequirement) return false;
  return hasPermission(sectionRequirement.feature, sectionRequirement.action);
};

/** True when the user may open at least one Back Office section. */
export const canAccessAnyBackofficeSection = (hasPermission: PermissionChecker): boolean => {
  const sectionTabs = new Set([
    ...Object.keys(BACKOFFICE_SECTION_PERMISSIONS),
    ...Object.keys(BACKOFFICE_SUBTAB_PERMISSIONS_BY_SECTION),
  ]);
  return [...sectionTabs].some((tab) => canAccessBackofficeSection(tab, hasPermission));
};
